/**
 * LeadSlideOver.tsx — Ficha de un prospecto como PANEL LATERAL IZQUIERDO
 * (drawer deslizante). Se abre al hacer click en un lead manteniendo el
 * kanban/tabla visible al lado. Disposición en UNA columna con scroll, SIN
 * pestañas (anti-patrón del research §4).
 *
 * Verdad base visual: `reference/mockups/pipeline-v2/index.html` («Agenda»).
 *
 *   CABECERA = inteligencia (nuestro diferencial): nombre, categoría,
 *   Opportunity Score (sello), señales y contacto accionable
 *   (Llamar/WhatsApp/Email/Maps con enlaces reales).
 *
 *   CUERPO = seguimiento CRM (donde el usuario trabaja):
 *     · «Próximo paso» destacado: texto corto + fecha con botones de fecha
 *       rápida (hoy / mañana / +3 días / próx. semana).
 *     · COMPOSER de nota siempre visible + selector de tipo
 *       (Nota / Llamada / Email / Reunión) como pills.
 *     · TIMELINE cronológico (más reciente arriba) mezclando notas manuales
 *       (en caja, editables/borrables) y eventos del sistema (líneas tenues).
 *
 * El seguimiento requiere que el lead esté en el pipeline (las notas se
 * cuelgan de `mp_user_pipeline`); si no lo está, se ofrece "Añadir al
 * pipeline". Backend: `/api/v1/listings/pipeline/:place_id/activity` (T-024).
 */
import { useEffect, useState, type ReactElement } from 'react'
import type { InboxItem, PipelineCard, PipelineStage, ActivityItem, ActivityType, SalesAngle } from '@/services/listingsService'
import {
  getLeadActivity,
  addNote as apiAddNote,
  updateNote as apiUpdateNote,
  deleteNote as apiDeleteNote,
  getSalesAngle,
} from '@/services/listingsService'
import { opportunityScore, leadSignals, contactability, type SignalKind } from '@/lib/opportunityScore'
import { categoryLabel } from '@/lib/categoryLabel'
import { formatDueHeadline, formatDueLong } from '@/lib/followUp'
import { DEMO_ACTIVITY, DEMO_ANGLE, DEMO_ANGLE_WITH_WEB } from '@/pages/dashboardDemo'
import ReviewsModal from '@/components/cabina/ReviewsModal'

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 } as const
const SEM_GLYPH = { high: '✓', mid: '!', low: '✕' } as const
const SIG_LABEL_FULL: Record<SignalKind, (l: string) => string> = {
  web: () => 'Sin web',
  claim: () => 'Ficha no reclamada',
  rev: (l) => l,
  star: (l) => `${l} · reputación alta`,
}

const IS_PREVIEW =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('preview')

function digits(phone: string | null): string {
  return (phone ?? '').replace(/[^\d]/g, '')
}

/** Ángulo de contacto pre-redactado citando el problema dominante (regla simple). */
function contactAngle(item: InboxItem): string {
  const hasWeb = !!(item.url && item.url.trim())
  const rating = item.rating_value
  const votes = item.rating_votes_count ?? 0
  const repTxt =
    rating != null
      ? `${rating.toString().replace('.', ',')}★${votes ? ` y ${votes} reseñas` : ''}`
      : 'buena reputación'
  if (!hasWeb) return `«Tienes ${repTxt} pero no apareces con una web propia.» Ofrécele diseño web: convertir su reputación en presencia online que capte clientes.`
  if (item.is_claimed === false) return `«Tienes ${repTxt}, pero tu ficha de Google no está reclamada.» Ofrécele gestión de su presencia: reclamar y optimizar la ficha para ganar visibilidad.`
  if (votes < 15) return `«Tu negocio gusta pero tiene pocas reseñas.» Ofrécele una estrategia de reputación para captar más opiniones y mejorar el posicionamiento local.`
  return `Negocio con margen de mejora en su presencia digital. Revisa sus señales para elegir el ángulo de venta.`
}

const STAGE_LABEL: Record<PipelineStage, string> = {
  nuevo: 'Nuevo', contactado: 'Contactado', propuesta: 'Propuesta', cerrado: 'Cerrado',
}

/** Web del negocio: ¿tiene? + dominio limpio + href absoluto (url o domain). */
function webOf(item: InboxItem): { has: boolean; label: string; href?: string } {
  const url = (item.url ?? '').trim()
  const domain = (item.domain ?? '').trim()
  if (!url && !domain) return { has: false, label: 'Sin web' }
  const label = domain || url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '')
  const href = url ? (/^https?:\/\//.test(url) ? url : `https://${url}`) : `https://${domain}`
  return { has: true, label, href }
}

/** Estado del negocio (GMB) en español. */
function statusLabelEs(status: string | null): string | null {
  if (status === 'open') return 'Abierto ahora'
  if (status === 'close') return 'Cerrado ahora'
  if (status === 'closed_forever') return 'Cerrado permanentemente'
  return null
}

/** Una fila de "Datos del negocio": etiqueta + valor (se omite si vacío). */
interface BizField { label: string; value: ReactElement | string; wide?: boolean }

/** Tono del badge del ángulo según el código (accionable vs contexto). */
const ANGLE_TONE: Record<SalesAngle['primary_code'], 'hot' | 'warm' | 'soft' | 'mute'> = {
  web_reservas: 'hot',
  visibilidad_contacto: 'hot',
  reputacion_resenas: 'warm',
  experiencia_servicio: 'soft',
  sin_angulo: 'mute',
}

/** Fecha local 'YYYY-MM-DD' desplazada `n` días respecto a hoy. */
function ymdOffset(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const QUICK_DATES: Array<{ key: string; label: string; offset: number }> = [
  { key: 'today', label: 'Hoy', offset: 0 },
  { key: 'tomorrow', label: 'Mañana', offset: 1 },
  { key: 'plus3', label: '+3 días', offset: 3 },
  { key: 'week', label: 'Próx. sem.', offset: 7 },
]

const TYPE_META: Record<ActivityType, { label: string; icon: ReactElement }> = {
  note: { label: 'Nota', icon: <svg viewBox="0 0 24 24" {...stroke}><path d="M5 4h11l3 3v13H5z" /><path d="M9 9h6M9 13h6" /></svg> },
  call: { label: 'Llamada', icon: <svg viewBox="0 0 24 24" {...stroke}><path d="M5 4h4l2 5-3 2a12 12 0 006 6l2-3 5 2v4a2 2 0 01-2 2A18 18 0 013 6a2 2 0 012-2z" /></svg> },
  email: { label: 'Email', icon: <svg viewBox="0 0 24 24" {...stroke}><path d="M3 8l9 6 9-6" /><rect x="3" y="5" width="18" height="14" rx="2" /></svg> },
  meeting: { label: 'Reunión', icon: <svg viewBox="0 0 24 24" {...stroke}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg> },
}
const TYPE_ORDER: ActivityType[] = ['note', 'call', 'email', 'meeting']

/** Texto humano de un evento del sistema del timeline. */
function eventText(a: ActivityItem): string {
  if (a.event_code === 'added_to_pipeline') return 'Añadido al pipeline'
  if (a.event_code === 'stage_changed') {
    const to = (a.event_meta as { to?: string } | null)?.to as PipelineStage | undefined
    return to ? `Movido a ${STAGE_LABEL[to] ?? to}` : 'Cambio de etapa'
  }
  return 'Actividad del sistema'
}

/** "hoy · 10:24" / "ayer · 17:02" / "3 jun · 09:10". */
function formatActivityTime(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const now = new Date()
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diffDays = Math.round((startToday.getTime() - dDay.getTime()) / (24 * 60 * 60 * 1000))
  const hh = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  if (diffDays === 0) return `hoy · ${hh}`
  if (diffDays === 1) return `ayer · ${hh}`
  const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${d.getDate()} ${MONTHS[d.getMonth()]} · ${hh}`
}

export default function LeadSlideOver({
  lead,
  card,
  onClose,
  pipelineStage = null,
  onAddToPipeline,
  onContact,
  onSaveNextStep,
}: {
  lead: InboxItem | null
  /** Tarjeta del pipeline (si el lead está en él): trae el próximo paso. */
  card?: PipelineCard | null
  onClose: () => void
  /** Stage actual del lead en el pipeline, o null si no está. */
  pipelineStage?: PipelineStage | null
  /** Añadir el lead al pipeline (stage 'nuevo'). Si se omite, se oculta la sección. */
  onAddToPipeline?: () => void
  /** Se llama al usar un canal de contacto (Llamar/WhatsApp/Email) → mover a 'contactado'. */
  onContact?: () => void
  /** Guardar el próximo paso (texto + fecha). El contenedor hace el PATCH optimista. */
  onSaveNextStep?: (next: { text: string | null; due: string | null }) => void
}) {
  const open = !!lead
  const inPipeline = pipelineStage != null

  // Timeline + composer.
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [draft, setDraft] = useState('')
  const [draftType, setDraftType] = useState<ActivityType>('note')
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')

  // Próximo paso (texto editable; la fecha la fija el card o los botones rápidos).
  const [stepText, setStepText] = useState('')

  // Reseñas + ángulo de venta (nuestro diferenciador).
  const [angle, setAngle] = useState<SalesAngle | null>(null)
  const [angleLoading, setAngleLoading] = useState(false)
  const [reviewsOpen, setReviewsOpen] = useState(false)

  const placeId = lead?.place_id ?? null
  const due = card?.next_step_due ?? null
  const status = card?.next_step_status ?? 'no_next_step'

  // Reset del estado local al cambiar de lead.
  useEffect(() => {
    setStepText(card?.next_step_text ?? '')
    setDraft('')
    setDraftType('note')
    setEditingId(null)
  }, [placeId, card?.next_step_text])

  // Cierre por Escape.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Cargar el ángulo de venta al abrir un lead (null si aún no se han
  // analizado sus reseñas → mostramos el CTA de "Ver reseñas").
  function loadAngle(signal?: AbortSignal) {
    if (!placeId) return
    if (IS_PREVIEW) {
      // En preview, el ángulo demo coherente con si el lead tiene web o no
      // (el de "sin web" trae la señal objetiva no_web v2).
      const hasW = !!((lead?.url ?? '').trim() || (lead?.domain ?? '').trim())
      setAngle(hasW ? DEMO_ANGLE_WITH_WEB : DEMO_ANGLE)
      return
    }
    setAngleLoading(true)
    getSalesAngle(placeId, signal)
      .then((a) => setAngle(a))
      .catch(() => setAngle(null))
      .finally(() => setAngleLoading(false))
  }
  useEffect(() => {
    if (!open || !placeId) { setAngle(null); setReviewsOpen(false); return }
    const ctrl = new AbortController()
    loadAngle(ctrl.signal)
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placeId])

  // Cargar el timeline al abrir un lead que esté en el pipeline.
  useEffect(() => {
    if (!open || !placeId || !inPipeline) {
      setActivity([])
      return
    }
    if (IS_PREVIEW) {
      setActivity(DEMO_ACTIVITY)
      return
    }
    let cancelled = false
    const ctrl = new AbortController()
    setActivityLoading(true)
    getLeadActivity(placeId, ctrl.signal)
      .then((r) => { if (!cancelled) setActivity(r.items) })
      .catch(() => { if (!cancelled) setActivity([]) })
      .finally(() => { if (!cancelled) setActivityLoading(false) })
    return () => { cancelled = true; ctrl.abort() }
  }, [open, placeId, inPipeline])

  // Hooks de inteligencia (siempre se ejecutan; fallback seguro).
  const sc = lead ? opportunityScore(lead) : null
  const signals = lead ? leadSignals(lead) : []
  const contact = lead ? contactability(lead) : { email: false, phone: false, whatsapp: false, count: 0 }
  const wa = digits(lead?.phone ?? null)
  const mapsUrl = lead?.check_url
    ? lead.check_url
    : lead
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lead.title} ${lead.address_components?.city ?? ''}`)}`
      : undefined

  // Web del negocio (Frente 1): dato que la tabla muestra pero la ficha no.
  const web = lead ? webOf(lead) : { has: false, label: 'Sin web' }

  // Datos del negocio (Frente 3): campos del catálogo de la tabla que faltaban
  // en la ficha. Se omiten los vacíos para no saturar.
  const ac = lead?.address_components ?? null
  // Campos de media columna primero (se emparejan en la cuadrícula 2-col) y los
  // anchos (dirección, categorías, descripción) al final para no dejar huecos.
  const bizHalf: BizField[] = []
  const bizWide: BizField[] = []
  if (lead) {
    const place = [ac?.city, ac?.region].filter(Boolean).join(' · ')
    if (place) bizHalf.push({ label: 'Ubicación', value: ac?.country_code ? `${place} · ${ac.country_code.toUpperCase()}` : place })
    if (ac?.zip) bizHalf.push({ label: 'Código postal', value: ac.zip })
    if (lead.rating_value != null) {
      bizHalf.push({
        label: 'Valoración Google',
        value: `${lead.rating_value.toString().replace('.', ',')} ★${lead.rating_votes_count ? ` · ${lead.rating_votes_count.toLocaleString('es-ES')} reseñas` : ''}`,
      })
    }
    if (lead.price_level) bizHalf.push({ label: 'Precio', value: lead.price_level })
    if (lead.total_photos != null) bizHalf.push({ label: 'Fotos en Google', value: lead.total_photos.toLocaleString('es-ES') })
    const st = statusLabelEs(lead.current_status)
    if (st) bizHalf.push({ label: 'Estado', value: st })
    if (lead.is_claimed != null) bizHalf.push({ label: 'Ficha reclamada', value: lead.is_claimed ? 'Sí' : 'No' })
    if (lead.address_full) bizWide.push({ label: 'Dirección', value: lead.address_full, wide: true })
    const cats = lead.additional_categories?.filter(Boolean) ?? []
    if (cats.length > 0) bizWide.push({ label: 'Otras categorías', value: cats.slice(0, 3).join(', ') + (cats.length > 3 ? '…' : ''), wide: true })
    if (lead.description) bizWide.push({ label: 'Descripción', value: lead.description, wide: true })
  }
  const bizFields: BizField[] = [...bizHalf, ...bizWide]

  function saveStep(text: string | null, dueDate: string | null) {
    onSaveNextStep?.({ text: text && text.trim() ? text.trim() : null, due: dueDate })
  }

  function onStepTextCommit() {
    const next = stepText.trim()
    if (next === (card?.next_step_text ?? '')) return
    saveStep(next || null, due)
  }

  function pickQuickDate(offset: number) {
    saveStep(stepText.trim() || card?.next_step_text || null, ymdOffset(offset))
  }

  function dueIsOffset(offset: number): boolean {
    return !!due && due === ymdOffset(offset)
  }

  async function submitNote() {
    const body = draft.trim()
    if (!body || !placeId) return
    setSaving(true)
    if (IS_PREVIEW) {
      const fake: ActivityItem = {
        id: `local-${Date.now()}`,
        place_id: placeId,
        kind: 'note',
        activity_type: draftType,
        body,
        event_code: null,
        event_meta: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      setActivity((a) => [fake, ...a])
      setDraft('')
      setSaving(false)
      return
    }
    try {
      const item = await apiAddNote(placeId, body, draftType)
      setActivity((a) => [item, ...a])
      setDraft('')
    } catch {
      // queda el borrador para reintentar
    } finally {
      setSaving(false)
    }
  }

  function startEdit(a: ActivityItem) {
    setEditingId(a.id)
    setEditText(a.body ?? '')
  }

  async function saveEdit(a: ActivityItem) {
    const body = editText.trim()
    if (!body || !placeId) { setEditingId(null); return }
    if (IS_PREVIEW) {
      setActivity((list) => list.map((x) => (x.id === a.id ? { ...x, body, updated_at: new Date().toISOString() } : x)))
      setEditingId(null)
      return
    }
    try {
      const updated = await apiUpdateNote(placeId, a.id, { body })
      setActivity((list) => list.map((x) => (x.id === a.id ? updated : x)))
    } catch {
      // mantener el original
    } finally {
      setEditingId(null)
    }
  }

  async function removeNote(a: ActivityItem) {
    if (!placeId) return
    const prev = activity
    setActivity((list) => list.filter((x) => x.id !== a.id))
    if (IS_PREVIEW) return
    try {
      await apiDeleteNote(placeId, a.id)
    } catch {
      setActivity(prev)
    }
  }

  // Preview: deep-link `?...&reviews` abre el tablero para la captura.
  useEffect(() => {
    if (!open || !placeId || !IS_PREVIEW) return
    if (new URLSearchParams(window.location.search).has('reviews')) setReviewsOpen(true)
  }, [open, placeId])

  // Nº de reseñas ya extraídas para este negocio (si el ángulo existe es que
  // ya se trajeron y analizaron → lo mostramos en el botón para no gastar otra
  // llamada sin querer). null si aún no hay reseñas.
  const reviewsCount = angle && angle.reviews_analyzed > 0 ? angle.reviews_analyzed : null

  const headline = formatDueHeadline(due, status)
  const dueLong = formatDueLong(due)

  return (
    <>
    <div
      className={`lead-drawer-wrap${open ? ' open' : ''}`}
      aria-hidden={!open}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <aside className="lead-drawer" onClick={(e) => e.stopPropagation()}>
        {lead && sc && (
          <>
            {/* ---- CABECERA = inteligencia ---- */}
            <div className="dr-head">
              <div className="dr-top">
                <div style={{ minWidth: 0 }}>
                  <div className="dr-name">{lead.title || '—'}</div>
                  <div className="dr-cat">
                    {categoryLabel(lead.category_primary)}
                    {lead.address_components?.city ? ` · ${lead.address_components.city}` : ''}
                  </div>
                </div>
                <span className={`score-chip ${sc.band}`}>
                  <span className={`sem ${sc.band}`}>{SEM_GLYPH[sc.band]}</span><b>{sc.score}</b>
                </span>
                <button className="dr-close" onClick={onClose} aria-label="Cerrar">
                  <svg viewBox="0 0 24 24" {...stroke}><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              </div>

              {signals.length > 0 && (
                <div className="dr-chips">
                  {signals.map((s) => (
                    <span className={`sig ${s.kind}`} key={s.label}>{SIG_LABEL_FULL[s.kind](s.label)}</span>
                  ))}
                </div>
              )}

              <div className="dr-contact">
                <a className={`cbtn-mini${contact.phone ? '' : ' disabled'}`} href={contact.phone ? `tel:${digits(lead.phone)}` : undefined} onClick={contact.phone ? () => onContact?.() : undefined}>
                  <svg viewBox="0 0 24 24" {...stroke}><path d="M5 4h4l2 5-3 2a12 12 0 006 6l2-3 5 2v4a2 2 0 01-2 2A18 18 0 013 6a2 2 0 012-2z" /></svg>Llamar
                </a>
                <a className={`cbtn-mini${contact.whatsapp ? '' : ' disabled'}`} href={contact.whatsapp ? `https://wa.me/${wa}` : undefined} target="_blank" rel="noopener noreferrer" onClick={contact.whatsapp ? () => onContact?.() : undefined}>
                  <svg viewBox="0 0 24 24" {...stroke}><path d="M21 11.5a8.4 8.4 0 01-12.3 7.4L3 21l2.1-5.7A8.4 8.4 0 1121 11.5z" /></svg>WhatsApp
                </a>
                <a className={`cbtn-mini${contact.email ? '' : ' disabled'}`} href={contact.email ? `mailto:${lead.email_primary}` : undefined} onClick={contact.email ? () => onContact?.() : undefined}>
                  <svg viewBox="0 0 24 24" {...stroke}><path d="M3 8l9 6 9-6" /><rect x="3" y="5" width="18" height="14" rx="2" /></svg>Email
                </a>
                <a className="cbtn-mini" href={mapsUrl} target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" /></svg>Maps
                </a>
              </div>

              {/* Ángulo de contacto sugerido (inteligencia del lead). */}
              <div className="dr-intel">
                <div className="sh">Ángulo de contacto sugerido</div>
                <div className="angle">{contactAngle(lead)}</div>
              </div>

              {/* Contactabilidad (eje aparte, NO suma al score). */}
              <div className="dr-intel">
                <div className="sh">Contactabilidad <span className="sh-soft">· eje aparte</span></div>
                <div className="contactability">
                  <span className={`cab-chip${contact.email ? ' on' : ''}`}><svg viewBox="0 0 24 24" {...stroke}><path d="M3 8l9 6 9-6" /><rect x="3" y="5" width="18" height="14" rx="2" /></svg>Email</span>
                  <span className={`cab-chip${contact.phone ? ' on' : ''}`}><svg viewBox="0 0 24 24" {...stroke}><path d="M5 4h4l2 5-3 2a12 12 0 006 6l2-3 5 2v4a2 2 0 01-2 2A18 18 0 013 6a2 2 0 012-2z" /></svg>Teléfono</span>
                  <span className={`cab-chip${contact.whatsapp ? ' on' : ''}`}><svg viewBox="0 0 24 24" {...stroke}><path d="M21 11.5a8.4 8.4 0 01-12.3 7.4L3 21l2.1-5.7A8.4 8.4 0 1121 11.5z" /></svg>WhatsApp</span>
                </div>
                {/* WEB del negocio (Frente 1): enlace si la tiene; "Sin web" si no
                    (es la señal de oportunidad). El dato vive en el lead pero no
                    se pintaba en la ficha. */}
                <div className="dr-web">
                  {web.has ? (
                    <a className="web-yes" href={web.href} target="_blank" rel="noopener noreferrer" title={web.href}>
                      <svg viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" /></svg>
                      <span className="web-dom">{web.label}</span>
                      <svg className="web-out" viewBox="0 0 24 24" {...stroke}><path d="M14 5h5v5M19 5l-9 9M11 5H6a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1v-5" /></svg>
                    </a>
                  ) : (
                    <span className="web-no">
                      <svg viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="9" /><path d="M5 5l14 14" /></svg>
                      Sin web propia <i>· oportunidad</i>
                    </span>
                  )}
                </div>
              </div>

              {/* DATOS DEL NEGOCIO (Frente 3): campos del catálogo de la tabla.
                  En el modal Trello viven al final de la COLUMNA IZQUIERDA, bajo
                  la inteligencia, para equilibrar la altura de las dos columnas. */}
              {bizFields.length > 0 && (
                <div className="dr-biz">
                  <div className="sh">Datos del negocio</div>
                  <dl className="biz-grid">
                    {bizFields.map((f) => (
                      <div className={`biz-row${f.wide ? ' wide' : ''}`} key={f.label}>
                        <dt>{f.label}</dt>
                        <dd>{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>

            {/* ---- COLUMNA DERECHA = ángulo de venta + seguimiento CRM ---- */}
            <div className="dr-body">
              {/* ÁNGULO DE VENTA — la jugada accionable (nuestro diferencial). */}
              <div className="dr-angle">
                <div className="da-top">
                  <span className="da-kicker">
                    <svg viewBox="0 0 24 24" {...stroke}><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>
                    Ángulo de venta
                  </span>
                  <button
                    type="button"
                    className={`da-reviews-btn${reviewsCount != null ? ' has' : ''}`}
                    onClick={() => setReviewsOpen(true)}
                    title={reviewsCount != null
                      ? `Ya tienes ${reviewsCount} reseñas extraídas — se abren sin volver a gastar una llamada`
                      : 'Trae las reseñas de Google de este negocio'}
                  >
                    <svg viewBox="0 0 24 24" {...stroke}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                    Ver reseñas
                    {reviewsCount != null && <span className="da-rv-count">{reviewsCount}</span>}
                  </button>
                </div>

                {angle ? (
                  <div className="da-body">
                    <div className={`da-headline ${ANGLE_TONE[angle.primary_code]}`}>{angle.primary_label}</div>
                    <p className="da-pitch">{angle.pitch}</p>
                    {angle.signals.length > 0 && (
                      <div className="da-signals">
                        {angle.signals.slice(0, 5).map((s) => {
                          // v2: las señales objetivas (dato del negocio) se pintan
                          // distintas de las de reseña (quejas). Análisis viejos
                          // sin `kind` → 'review'.
                          const objective = (s.kind ?? 'review') === 'objective'
                          return (
                            <span
                              className={`da-sig${objective ? ' obj' : s.in_negative > 0 ? ' neg' : ''}`}
                              key={s.code}
                              title={s.evidence[0] ?? ''}
                            >
                              {objective ? (
                                <svg className="da-sig-ic" viewBox="0 0 24 24" {...stroke}><path d="M4 21V8l8-5 8 5v13M9 21v-6h6v6" /></svg>
                              ) : (
                                <svg className="da-sig-ic" viewBox="0 0 24 24" {...stroke}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                              )}
                              {s.label}
                              {objective ? (
                                <i className="da-tag">dato del negocio</i>
                              ) : (
                                <>
                                  <b>{s.mentions}</b>
                                  {s.in_negative > 0 && <i className="da-neg">{s.in_negative} en quejas</i>}
                                </>
                              )}
                            </span>
                          )
                        })}
                      </div>
                    )}
                    <div className="da-foot">
                      Sobre {angle.reviews_analyzed} reseña{angle.reviews_analyzed === 1 ? '' : 's'} analizada{angle.reviews_analyzed === 1 ? '' : 's'}
                      {angle.negative_reviews > 0 ? ` · ${angle.negative_reviews} negativas` : ''}
                    </div>
                  </div>
                ) : angleLoading ? (
                  <div className="da-empty">Analizando reseñas…</div>
                ) : (
                  <div className="da-empty">
                    <p className="da-empty-t">Aún no has analizado sus reseñas.</p>
                    <p className="da-empty-s">Trae las reseñas de Google y te destilamos la <b>jugada de venta</b>: qué quejas repiten y qué ofrecerle.</p>
                  </div>
                )}
              </div>

              {!inPipeline ? (
                <button type="button" className="btn btn-accent dr-add-pipeline" onClick={() => onAddToPipeline?.()}>
                  <svg viewBox="0 0 24 24" {...stroke}><path d="M12 5v14M5 12h14" /></svg>
                  Añadir al pipeline para empezar el seguimiento
                </button>
              ) : (
                <>
                  {/* PRÓXIMO PASO */}
                  <div className="sh">Próximo paso</div>
                  <div className="nextcard">
                    <div className="nc-top">
                      <span className="nc-ic">
                        <svg viewBox="0 0 24 24" {...stroke}><path d="M5 4h4l2 5-3 2a12 12 0 006 6l2-3 5 2v4a2 2 0 01-2 2A18 18 0 013 6a2 2 0 012-2z" /></svg>
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="nc-lab">Próxima acción</div>
                        <input
                          className="nc-input"
                          value={stepText}
                          placeholder="p. ej. Llamar para enseñar la auditoría"
                          onChange={(e) => setStepText(e.target.value)}
                          onBlur={onStepTextCommit}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                          maxLength={280}
                        />
                      </div>
                      {headline && (
                        <div className="nc-date">
                          <div className={`d${status === 'overdue' ? ' overdue' : ''}`}>{headline}</div>
                          {dueLong && <div className="dl">{dueLong}</div>}
                        </div>
                      )}
                    </div>
                    <div className="qrow">
                      {QUICK_DATES.map((q) => (
                        <button
                          key={q.key}
                          type="button"
                          className={`qd${dueIsOffset(q.offset) ? ' on' : ''}`}
                          onClick={() => pickQuickDate(q.offset)}
                        >
                          {q.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* COMPOSER */}
                  <div className="composer">
                    <div className="sh">Añadir seguimiento</div>
                    <div className="typepills">
                      {TYPE_ORDER.map((t) => (
                        <button
                          key={t}
                          type="button"
                          className={`tp${draftType === t ? ' on' : ''}`}
                          onClick={() => setDraftType(t)}
                        >
                          {TYPE_META[t].icon}{TYPE_META[t].label}
                        </button>
                      ))}
                    </div>
                    <textarea
                      className="ta"
                      placeholder="Añadir nota…  (qué hablasteis, próximos pasos)"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); submitNote() } }}
                      maxLength={4000}
                    />
                    <div className="cmp-foot">
                      <span className="cmp-hint">Guardar con ⌘ + Enter</span>
                      <button type="button" className="btn-save" onClick={submitNote} disabled={!draft.trim() || saving}>
                        <svg viewBox="0 0 24 24" {...stroke}><path d="M5 12l5 5 9-10" /></svg>Guardar
                      </button>
                    </div>
                  </div>

                  {/* TIMELINE */}
                  <div className="sh">Actividad</div>
                  <div className="tl">
                    {activityLoading && activity.length === 0 ? (
                      <div className="tl-empty">Cargando actividad…</div>
                    ) : activity.length === 0 ? (
                      <div className="tl-empty">Sin actividad todavía. Añade una nota o fija el próximo paso.</div>
                    ) : (
                      activity.map((a) =>
                        a.kind === 'note' ? (
                          <div className="note-box" key={a.id}>
                            <div className="nb-h">
                              <span className="nb-type">{TYPE_META[a.activity_type ?? 'note'].icon}{TYPE_META[a.activity_type ?? 'note'].label}</span>
                              <span className="nb-time">{formatActivityTime(a.created_at)}</span>
                              {editingId !== a.id && (
                                <span className="nb-acts">
                                  <button onClick={() => startEdit(a)}>Editar</button>
                                  <button onClick={() => removeNote(a)}>Borrar</button>
                                </span>
                              )}
                            </div>
                            {editingId === a.id ? (
                              <>
                                <textarea
                                  className="nb-edit"
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  autoFocus
                                  maxLength={4000}
                                />
                                <div className="nb-edit-foot">
                                  <button className="primary" onClick={() => saveEdit(a)}>Guardar</button>
                                  <button onClick={() => setEditingId(null)}>Cancelar</button>
                                </div>
                              </>
                            ) : (
                              <div className="nb-txt">{a.body}</div>
                            )}
                          </div>
                        ) : (
                          <div className="sys-line" key={a.id}>
                            <span className="sl-ic">
                              <svg viewBox="0 0 24 24" {...stroke}><path d="M5 12l5 5 9-10" /></svg>
                            </span>
                            <span className="sl-txt"><b>{eventText(a)}</b></span>
                            <span className="sl-time">{formatActivityTime(a.created_at)}</span>
                          </div>
                        ),
                      )
                    )}
                  </div>
                </>
              )}

              {/* ---- Secundario: ganchos premium (funciones futuras). Al final,
                   diferenciados, para NO competir con el próximo paso ni el timeline. ---- */}
              <div className="dr-premium">
                {/* Plantilla de contacto con IA (muestra). */}
                <div className="prem-block">
                  <div className="sh">Plantilla de contacto <span className="badge soon">Próximamente</span></div>
                  <div className="tmpl">
                    <div className="tmpl-txt">
                      Hola, soy [tu nombre]. He visto que <b>{lead.title}</b>{' '}
                      <span className="blur">tiene una reputación excelente pero su presencia online es mejorable. Podríamos ayudaros a captar más clientes con…</span>
                    </div>
                    <div className="tmpl-over"><span className="lock"><svg viewBox="0 0 24 24" {...stroke}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>Mensaje con IA · plan superior</span></div>
                  </div>
                </div>

                {/* Inteligencia web ampliada del negocio (teaser premium futuro:
                    SEO/tráfico/tecnología — distinta del ángulo de reseñas que
                    ya es REAL arriba). */}
                <div className="prem-block">
                  <div className="sh">Inteligencia web ampliada <span className="badge soon">Próximamente</span></div>
                  <div className="intel-premium">
                    <div className="ip-row"><span className="ip-ic"><svg viewBox="0 0 24 24" {...stroke}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg></span><span className="ip-name">Visibilidad SEO estimada</span><span className="skel" style={{ marginLeft: 'auto' }} /></div>
                    <div className="ip-row"><span className="ip-ic"><svg viewBox="0 0 24 24" {...stroke}><path d="M3 17l6-6 4 4 8-8" /></svg></span><span className="ip-name">Tráfico web estimado</span><span className="skel" style={{ marginLeft: 'auto' }} /></div>
                    <div className="ip-row"><span className="ip-ic"><svg viewBox="0 0 24 24" {...stroke}><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M8 20h8" /></svg></span><span className="ip-name">Tecnología y velocidad web</span><span className="lock" style={{ marginLeft: 'auto' }}><svg viewBox="0 0 24 24" {...stroke}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg></span></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>

    <ReviewsModal
      lead={lead}
      open={reviewsOpen}
      onClose={() => setReviewsOpen(false)}
      onCompleted={() => loadAngle()}
    />
    </>
  )
}
